import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, Flex, Icon, Text, Tooltip } from 'core/components';
import { InputGroup } from 'core/form';
import { TbChevronDown, TbChevronRight, TbChevronsLeft, TbDatabase, TbMath } from 'react-icons/tb';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import type { IconType } from 'react-icons';
import { ReactComponent as TrafficIcon } from 'app/assets/icons/ude/traffic.svg';
import { ReactComponent as NmsIcon } from 'app/assets/icons/ude/nms.svg';
import { ReactComponent as SyntheticsIcon } from 'app/assets/icons/ude/synthetics.svg';
import { ReactComponent as EventsIcon } from 'app/assets/icons/ude/events.svg';
import { ReactComponent as CloudIcon } from 'app/assets/icons/ude/cloud.svg';
import SidebarSection from '../../SidebarSection';
import { titleExactMatches } from './metricsTreeData';
import { extractSubCategoriesDeep, SubCategory } from './categoryUtils';

interface Category {
  key: string;
  label: string;
  icon: string | IconType | React.ComponentType;
  enabled: boolean;
  hideChildren?: boolean;
}

const CATEGORIES: Category[] = [
  {
    key: '/traffic',
    label: 'Traffic',
    icon: TrafficIcon,
    enabled: true,
    hideChildren: true
  },
  { key: '/metrics', label: 'NMS', icon: NmsIcon, enabled: true },
  { key: '/synthetics', label: 'Synthetics', icon: SyntheticsIcon, enabled: true },
  { key: '/events', label: 'Events', icon: EventsIcon, enabled: true },
  { key: '/cloud', label: 'Cloud', icon: CloudIcon, enabled: false },
  { key: '/bgp', label: 'BGP', icon: 'search-around', enabled: false },
  { key: '/cost', label: 'Cost Analysis', icon: 'dollar', enabled: false }
];

interface SearchResultItem {
  value: string;
  label: string;
  type: 'metric' | 'dimension' | 'measurement';
  measurement: string;
  categoryPrefix: string;
  categoryLabel: string;
  /** Pre-computed lowercase haystack for search matching */
  searchText: string;
}

interface CategoryPanelProps {
  selectedCategory: string | null;
  onSelectCategory: (categoryPrefix: string) => void;
  measurements: Measurement[];
  /** Called when a user selects an item from global search results */
  onSearchSelect: (item: SearchResultItem) => void;
  /** Optional: collapse the parent sidebar. When provided, a collapse button is rendered. */
  onToggleCollapse?: () => void;
}

interface CategoryRowStyleProps {
  $isSelected: boolean;
  $isDisabled: boolean;
}

const SearchContainer = styled(Box)`
  padding: 2px 22px 6px 18px;
  flex-shrink: 0;

  .bp4-input {
    font-size: 13px;
  }

  .bp4-icon {
    top: 1px;
    left: 2px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const CategoryList = styled(Flex)`
  padding: 8px;
  padding-top: 0;
  --connector-line-color: ${(props) => props.theme.borderColors.thin};
`;

const ResultsContainer = styled(Box)`
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
`;

const CategoryRow = styled(Button)<CategoryRowStyleProps>`
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  font-size: 14px;
  color: ${(props) => (props.$isSelected ? props.theme.colors.primary : props.theme.colors.body)};
  font-weight: 500;

  opacity: ${(props) => (props.$isDisabled ? 0.5 : 1)};

  &:hover {
    background-color: ${(props) => {
      if (props.$isDisabled) {
        return 'transparent';
      }

      return props.$isSelected
        ? props.theme.colors.primaryBackground
        : props.theme.colors.sectionBackground || '#f0f0f0';
    }};
  }
`;

const SubCategoryRow = styled.button<{ $isSelected: boolean; $depth?: number }>`
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
  position: relative;
  padding: 4px 12px 4px ${(props) => 28 + (props.$depth ?? 1) * 14}px;
  border: none;
  border-radius: 4px;
  background-color: ${(props) => (props.$isSelected ? props.theme.colors.primaryBackground : 'transparent')};
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.theme.colors.body};
  text-align: left;

  .category-description {
    opacity: 0;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.sectionBackground || '#f0f0f0'};

    .category-description {
      opacity: 1;
    }
  }
`;

// --- Helpers ---

const MAX_SEARCH_RESULTS = 200;

function getCategoryPrefixFromPath(path: string): string | null {
  const match = path.match(/^(\/[^/]+)/);
  return match ? match[1] : null;
}

function getCategoryLabelForPrefix(prefix: string): string {
  const cat = CATEGORIES.find((c) => c.key === prefix);
  return cat?.label ?? prefix.replace('/', '');
}

/**
 * Build a flat search index directly from measurements.
 * Avoids constructing the full Blueprint tree (with React elements, sorting,
 * directional grouping, etc.) just to extract leaf items.
 */
function buildSearchIndex(measurements: Measurement[]): SearchResultItem[] {
  const results: SearchResultItem[] = [];

  // Pre-compute a set of enabled category prefixes for O(1) lookup
  const enabledPrefixes = new Set(CATEGORIES.filter((c) => c.enabled).map((c) => c.key));

  measurements.forEach((measurementObj) => {
    const path = measurementObj.measurement;
    if (!path) {
      return;
    }

    const categoryPrefix = getCategoryPrefixFromPath(path);
    if (!categoryPrefix || !enabledPrefixes.has(categoryPrefix)) {
      return;
    }

    const categoryLabel = getCategoryLabelForPrefix(categoryPrefix);
    const { Metrics = {}, Dimensions = {} } = measurementObj.storage || {};

    // Add measurement-level entry so searching "cpus" surfaces the measurement itself
    const pathSegments = path.split('/').filter(Boolean);
    const measurementLabel =
      measurementObj.displayName ||
      titleExactMatches[pathSegments[pathSegments.length - 1]] ||
      pathSegments[pathSegments.length - 1];
    results.push({
      value: path,
      label: measurementLabel,
      type: 'measurement',
      measurement: path,
      categoryPrefix,
      categoryLabel,
      searchText: [measurementLabel, path, categoryLabel].join(' ').toLowerCase()
    });

    Object.entries(Metrics).forEach(([key, value]) => {
      // NOTE: include Shared items. They're stored only on the root measurement
      // (e.g. /traffic) and inherited into derived measurements at query time,
      // so omitting them here makes core items like 'bits' unsearchable.
      const label = value.Label || key;
      results.push({
        value: key,
        label,
        type: 'metric',
        measurement: path,
        categoryPrefix,
        categoryLabel,
        searchText: [label, key, path, categoryLabel].join(' ').toLowerCase()
      });
    });

    Object.entries(Dimensions).forEach(([key, value]) => {
      if (!value.FilterOnly) {
        const label = value.Label || key;
        results.push({
          value: key,
          label,
          type: 'dimension',
          measurement: path,
          categoryPrefix,
          categoryLabel,
          searchText: [label, key, path, categoryLabel].join(' ').toLowerCase()
        });
      }
    });
  });

  return results;
}

// --- Search Filtering Helpers ---

interface SearchMatchResult {
  /** Measurement paths that have at least one matching item */
  matchedMeasurements: Set<string>;
  /** Measurement path → matching leaf items (metrics + dimensions) grouped */
  matchedItemsByMeasurement: Map<string, SearchResultItem[]>;
}

function getSearchMatches(query: string, allItems: SearchResultItem[]): SearchMatchResult {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return { matchedMeasurements: new Set(), matchedItemsByMeasurement: new Map() };
  }

  const matchedMeasurements = new Set<string>();
  const matchedItemsByMeasurement = new Map<string, SearchResultItem[]>();
  let count = 0;

  for (const item of allItems) {
    if (count >= MAX_SEARCH_RESULTS) {
      break;
    }

    if (tokens.every((token) => item.searchText.includes(token))) {
      matchedMeasurements.add(item.measurement);
      if (item.type !== 'measurement') {
        const existing = matchedItemsByMeasurement.get(item.measurement) || [];
        existing.push(item);
        matchedItemsByMeasurement.set(item.measurement, existing);
      }
      count += 1;
    }
  }

  return { matchedMeasurements, matchedItemsByMeasurement };
}

function filterSubCategories(subs: SubCategory[], matchedMeasurements: Set<string>): SubCategory[] {
  return subs.reduce<SubCategory[]>((acc, sub) => {
    // Check if this subcategory's key IS a matched measurement
    if (matchedMeasurements.has(sub.key)) {
      acc.push(sub);
      return acc;
    }

    // Check if any children lead to a matched measurement
    if (sub.children && sub.children.length > 0) {
      const filteredChildren = filterSubCategories(sub.children, matchedMeasurements);
      if (filteredChildren.length > 0) {
        acc.push({ ...sub, children: filteredChildren });
      }
    }

    return acc;
  }, []);
}

const MeasurementResultRow = styled.button<{ $depth?: number }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  position: relative;
  padding: 4px 12px 4px ${(props) => 28 + 16 + (props.$depth ?? 2) * 14}px;
  min-height: 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  background-color: transparent;
  color: ${(props) => props.theme.colors.body};

  &:hover {
    background-color: ${(props) => props.theme.colors.sectionBackground || '#f0f0f0'};
  }
`;

// --- Main Component ---

const CategoryPanel = ({
  selectedCategory,
  onSelectCategory,
  measurements,
  onSearchSelect,
  onToggleCollapse
}: CategoryPanelProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => new Set(CATEGORIES.filter((c) => c.enabled).map((c) => c.key))
  );
  const [expandedSubCategories, setExpandedSubCategories] = useState<Set<string>>(new Set());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchQuery(value);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setDebouncedQuery(value.trim());
    }, 200);
  }, []);

  // Cleanup timer on unmount
  useEffect(
    () => () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    },
    []
  );

  // Build sub-categories map for categories that have 2+ sub-categories (with 2 extra levels)
  const subCategoriesMap = useMemo(() => {
    const map = new Map<string, SubCategory[]>();
    for (const cat of CATEGORIES) {
      if (cat.enabled) {
        const subs = extractSubCategoriesDeep(measurements, cat.key, 2);
        if (subs.length >= 2) {
          map.set(cat.key, subs);
        }
      }
    }
    return map;
  }, [measurements]);

  // Build flat search index directly from measurements (no tree construction needed)
  const allItems = useMemo(() => {
    if (!measurements || measurements.length === 0) {
      return [];
    }
    return buildSearchIndex(measurements);
  }, [measurements]);

  // Accordion mode: toggle expansion
  const handleAccordionClick = useCallback(
    (category: Category) => {
      if (!category.enabled) {
        return;
      }

      const subs = subCategoriesMap.get(category.key);
      if (subs && subs.length >= 2) {
        setExpandedCategories((prev) => {
          const next = new Set(prev);
          if (next.has(category.key)) {
            next.delete(category.key);
          } else {
            next.add(category.key);
          }
          return next;
        });
      } else {
        onSelectCategory(category.key);
      }
    },
    [subCategoriesMap, onSelectCategory]
  );

  const isSearching = debouncedQuery.length > 0;

  const searchMatches = useMemo(() => {
    if (!debouncedQuery) {
      return null;
    }
    return getSearchMatches(debouncedQuery, allItems);
  }, [debouncedQuery, allItems]);

  const toggleSubCategory = useCallback((key: string) => {
    setExpandedSubCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const renderSubCategory = (sub: SubCategory, depth: number): React.ReactNode => {
    const hasChildren = sub.children && sub.children.length > 0;
    const isSubExpanded = isSearching || expandedSubCategories.has(sub.key);
    const isSelected = selectedCategory === sub.key;

    // Synthetic leaf nodes that represent a measurement which is also a parent
    // (see categoryUtils.extractSubCategoriesDeep for details).
    const isSyntheticLeaf = !!sub.isMeasurement && !hasChildren;

    // When searching, get inline items for this measurement.
    // Skip for synthetic leaves — the parent group already renders them.
    const inlineItems = isSyntheticLeaf ? undefined : searchMatches?.matchedItemsByMeasurement.get(sub.key);

    // During search, only leaf measurements with matching items are clickable.
    // Synthetic leaves are always clickable since they represent a selectable measurement.
    const isClickable = isSyntheticLeaf || !isSearching || (!hasChildren && !!inlineItems);

    // Use a distinct React key for synthetic leaves to avoid colliding with the parent
    const reactKey = isSyntheticLeaf ? `${sub.key}/__self__` : sub.key;

    return (
      <React.Fragment key={reactKey}>
        <SubCategoryRow
          $isSelected={isSelected}
          $depth={depth}
          style={isSearching && !isClickable ? { cursor: 'default' } : undefined}
          onClick={() => {
            if (hasChildren && !isSearching) {
              toggleSubCategory(sub.key);
            } else if (isClickable) {
              onSelectCategory(sub.key);
            }
          }}
        >
          {/* eslint-disable-next-line no-nested-ternary */}
          {hasChildren && !isSearching ? (
            isSubExpanded ? (
              <TbChevronDown size={10} />
            ) : (
              <TbChevronRight size={10} />
            )
          ) : (
            <Icon icon="blank" iconSize={10} />
          )}
          <Text as="span" flex={1}>
            {sub.label}
          </Text>
        </SubCategoryRow>
        {isSubExpanded && sub.children?.map((child) => renderSubCategory(child, depth + 1))}
        {/* Inline search results under this measurement */}
        {isSearching &&
          inlineItems &&
          inlineItems.map((item) => (
            <MeasurementResultRow
              key={`${item.measurement}/${item.value}`}
              $depth={depth + 1}
              onClick={() => onSearchSelect(item)}
            >
              <Icon
                icon={item.type === 'metric' ? TbMath : TbDatabase}
                color={item.type === 'metric' ? 'success' : 'primary'}
              />
              <Text as="span" flex={1}>
                {item.label}
              </Text>
            </MeasurementResultRow>
          ))}
      </React.Fragment>
    );
  };

  const renderCategoryRow = (category: Category) => {
    const isSelected = selectedCategory?.startsWith(category.key) ?? false;
    const isDisabled = !category.enabled;

    let categoryTitle = category.label;
    if (isDisabled) {
      categoryTitle = `${category.label} (not yet supported)`;
    }

    const subs = subCategoriesMap.get(category.key);

    // When searching, filter subcategories to only matching branches
    let displaySubs;
    if (isSearching && subs && searchMatches) {
      displaySubs = filterSubCategories(subs, searchMatches.matchedMeasurements);
    } else if (!category.hideChildren) {
      displaySubs = subs ?? [];
    }

    // Hide category entirely if searching and nothing matches
    if (isSearching && (!displaySubs || displaySubs.length === 0)) {
      if (!searchMatches?.matchedMeasurements.has(category.key)) {
        return null;
      }
    }

    // Auto-expand when searching
    const isExpanded = isSearching || expandedCategories.has(category.key);

    if (isDisabled) {
      return null;
    }

    return (
      <React.Fragment key={category.key}>
        <CategoryRow
          minimal
          fill
          textAlign="left"
          $isSelected={isSelected}
          $isDisabled={isDisabled}
          onClick={() => {
            if (category.hideChildren) {
              onSelectCategory(category.key);
            } else if (!isDisabled && !isSearching) {
              handleAccordionClick(category);
            }
          }}
          title={categoryTitle}
        >
          <Flex alignItems="center" gap={1}>
            {category.icon && <Icon icon={category.icon} color="kentik" size={14} />}
            <Text as="span" flex={1}>
              {category.label}
            </Text>
          </Flex>
        </CategoryRow>

        {isExpanded &&
          (() => {
            const directItems = isSearching ? searchMatches?.matchedItemsByMeasurement.get(category.key) : undefined;
            const hasDirectItems = directItems && directItems.length > 0;

            return (
              <>
                {displaySubs?.map((sub) => renderSubCategory(sub, 0))}

                {/* Direct measurement match for categories that ARE measurements (e.g. /traffic) */}
                {hasDirectItems &&
                  directItems.map((item) => (
                    <MeasurementResultRow
                      key={`${item.measurement}/${item.value}`}
                      $depth={1}
                      onClick={() => onSearchSelect(item)}
                    >
                      <Icon
                        icon={item.type === 'metric' ? TbMath : TbDatabase}
                        color={item.type === 'metric' ? 'success' : 'primary'}
                        size={10}
                      />
                      <Text as="span" flex={1}>
                        {item.label}
                      </Text>
                    </MeasurementResultRow>
                  ))}
              </>
            );
          })()}
      </React.Fragment>
    );
  };

  return (
    <Flex flexDirection="column" flex={1} minHeight={0}>
      <SidebarSection
        className="ude-measurement-header"
        isCollapsible={false}
        borderBottom="none"
        pl="8px"
        pr="4px"
        title={
          <Text as="div" fontSize={14} fontWeight="bold">
            Build your query
          </Text>
        }
        rightElement={
          onToggleCollapse && (
            <Flex ml="auto" alignItems="center">
              <Tooltip content="Close Query options">
                <Button minimal icon={TbChevronsLeft} onClick={onToggleCollapse} aria-label="Close Query options" />
              </Tooltip>
            </Flex>
          )
        }
      >
        {null}
      </SidebarSection>
      <SearchContainer>
        <InputGroup
          type="search"
          fill
          leftIcon="search"
          placeholder="Search units and dimensions..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </SearchContainer>
      <ResultsContainer>
        <CategoryList flexDirection="column">{CATEGORIES.map((category) => renderCategoryRow(category))}</CategoryList>
        {isSearching && searchMatches && searchMatches.matchedMeasurements.size === 0 && (
          <Flex p={3} justifyContent="center">
            <Text muted small>
              No results found
            </Text>
          </Flex>
        )}
      </ResultsContainer>
    </Flex>
  );
};

export default CategoryPanel;

export { CATEGORIES };
export type { Category, SearchResultItem };
